"use strict";
// Copyright © Veeam Software Group GmbH
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScheduleHarmonizationManager = void 0;
const baseTypes_1 = require("../../core/baseTypes");
const constants_1 = require("./constants");
const helpers_1 = require("./helpers");
const intersect_1 = require("./helpers/intersect");
const types_1 = require("./types");
class ScheduleHarmonizationManager {
    constructor(scheduleModels) {
        this.scheduleModels = scheduleModels;
        this.getDailyScheduleModelValue = () => {
            return this.scheduleModels.dailyScheduleModel.getValue();
        };
        this.getWeeklyScheduleModelValue = () => {
            return this.scheduleModels.weeklyScheduleModel.getValue();
        };
        this.getMonthlyScheduleModelValue = () => {
            return this.scheduleModels.monthlyScheduleModel.getValue();
        };
        this.getYearlyScheduleModelValue = () => {
            return this.scheduleModels.yearlyScheduleModel.getValue();
        };
        this.getScheduleModelValues = () => {
            return {
                daily: this.getDailyScheduleModelValue(),
                weekly: this.getWeeklyScheduleModelValue(),
                monthly: this.getMonthlyScheduleModelValue(),
                yearly: this.getYearlyScheduleModelValue(),
            };
        };
        this.canArchiveBeEnabled = () => {
            const dailyCurrentState = this.scheduleModels.dailyScheduleModel.getValue();
            const weeklyCurrentState = this.scheduleModels.weeklyScheduleModel.getValue();
            const dailyThirdRowSchedule = dailyCurrentState.rows.third.schedule.filter(Boolean).length;
            const weeklyThirdRowSchedule = weeklyCurrentState.rows.third.schedule.filter(Boolean).length;
            return dailyThirdRowSchedule + weeklyThirdRowSchedule > 0;
        };
        this.scheduleModels = scheduleModels;
    }
}
exports.ScheduleHarmonizationManager = ScheduleHarmonizationManager;
ScheduleHarmonizationManager.ROW_FIRST_FIELD_COUNT_INITIAL_VALUE = constants_1.DEFAULT_FIRST_ROW_RETENTION_COUNT;
ScheduleHarmonizationManager.ROW_SECOND_FIELD_COUNT_INITIAL_VALUE = constants_1.DEFAULT_SECOND_ROW_RETENTION_COUNT;
ScheduleHarmonizationManager.ROW_THIRD_FIELD_COUNT_INITIAL_VALUE = constants_1.DEFAULT_THIRD_ROW_RETENTION_COUNT;
ScheduleHarmonizationManager.ROW_THIRD_FIELD_TYPE_INITIAL_VALUE = baseTypes_1.RETENTION_TYPE.days;
ScheduleHarmonizationManager.ROW_ANY_FIELD_COUNT_MIN_VALUE = constants_1.RETENTION_COUNT_MIN_NOT_DAILY;
ScheduleHarmonizationManager.ROW_ANY_FIELD_COUNT_MAX_VALUE = constants_1.RETENTION_COUNT_MAX;
ScheduleHarmonizationManager.FIELD_TIME_INITIAL_VALUE = constants_1.DEFAULT_TIME_LOCAL;
ScheduleHarmonizationManager.FIELD_DAY_NUMBER_IN_MONTH = types_1.DAY_NUMBER_IN_MONTH.first;
ScheduleHarmonizationManager.FIELD_DAY_OF_WEEK = baseTypes_1.DAY_OF_WEEK.sunday;
ScheduleHarmonizationManager.FIELD_DAY_OF_MONTH = '1';
ScheduleHarmonizationManager.getRowInitialValue = (isEnabled, scheduleItemsCount, fieldCountValue, fieldTypeValue, isTypeVisible) => {
    return {
        isEnabled,
        fields: Object.assign(Object.assign({ count: fieldCountValue }, (fieldTypeValue ? { type: fieldTypeValue } : {})), { isTypeVisible }),
        schedule: helpers_1.getEmptyColumns(scheduleItemsCount),
    };
};
ScheduleHarmonizationManager.getRowFirstInitialValue = (isEnabled, scheduleItemsCount, fieldCountValue, fieldTypeValue, isRowFieldTypeVisible) => {
    return ScheduleHarmonizationManager.getRowInitialValue(isEnabled, scheduleItemsCount, fieldCountValue, fieldTypeValue, isRowFieldTypeVisible);
};
ScheduleHarmonizationManager.getRowSecondInitialValue = (isEnabled, scheduleItemsCount, fieldCountValue, fieldTypeValue, isRowFieldTypeVisible) => {
    return ScheduleHarmonizationManager.getRowInitialValue(isEnabled, scheduleItemsCount, fieldCountValue, fieldTypeValue, isRowFieldTypeVisible);
};
ScheduleHarmonizationManager.getRowThirdInitialValue = (isEnabled, scheduleItemsCount, fieldCountValue, fieldTypeValue, isRowFieldTypeVisible) => {
    return ScheduleHarmonizationManager.getRowInitialValue(isEnabled, scheduleItemsCount, fieldCountValue, fieldTypeValue, isRowFieldTypeVisible);
};
ScheduleHarmonizationManager.getWeeklyMonthlyFieldsInitialValue = () => {
    return {
        dayNumberInMonth: ScheduleHarmonizationManager.FIELD_DAY_NUMBER_IN_MONTH,
        dayOfWeek: ScheduleHarmonizationManager.FIELD_DAY_OF_WEEK,
        dayOfMonth: ScheduleHarmonizationManager.FIELD_DAY_OF_MONTH,
        time: ScheduleHarmonizationManager.FIELD_TIME_INITIAL_VALUE,
    };
};
ScheduleHarmonizationManager.getCurrentOrFirstInListItem = (currentItem, availableItems) => {
    let restorePoint = currentItem;
    if (!availableItems.includes(restorePoint)) {
        restorePoint = availableItems[0];
    }
    return restorePoint;
};
ScheduleHarmonizationManager.getCurrentAndPrevIntersectedOptions = (currentOptions, prevOptions) => {
    let resultOptions = intersect_1.intersect(prevOptions, currentOptions);
    if (!resultOptions.length) {
        resultOptions = prevOptions;
    }
    return resultOptions;
};
// get snapshots or backups
ScheduleHarmonizationManager.getFirstOrThirdRowSchedule = (shouldGetFirst, schedule) => {
    return shouldGetFirst ? schedule.firstRow : schedule.thirdRow;
};
ScheduleHarmonizationManager.getAvailableOptionsFieldRow = (currentState, availableOptions) => {
    const result = ScheduleHarmonizationManager.getCurrentAndPrevIntersectedOptions(currentState, availableOptions);
    return result;
};
ScheduleHarmonizationManager.getAvailableOptionsRow = (row, currentState, availableOptions) => {
    return ScheduleHarmonizationManager.getAvailableOptionsFieldRow(currentState[row], availableOptions[row]);
};
// Maksim.Kovalevskii: see Policy Schedule docs (section "The problem with harmonization of archived backups when weekly schedule is enabled")
// https://rnd-confluence.veeam.local/pages/viewpage.action?spaceKey=VbForAws&title=Schedule
ScheduleHarmonizationManager.getAvailableOptionsTimeThirdRow = (shouldMerge, currentState, availableOptions) => {
    const weeklyTimeThirdRow = currentState.thirdRow;
    const merged = shouldMerge
        ? Array.from(new Set(availableOptions.thirdRow.concat(weeklyTimeThirdRow)).values())
        : availableOptions.thirdRow;
    return ScheduleHarmonizationManager.getCurrentAndPrevIntersectedOptions(weeklyTimeThirdRow, merged);
};
//# sourceMappingURL=ScheduleHarmonizationManager.js.map